// kanjo/Reports.jsx — Taxes / Payroll / Census Bureau report screens.
//
// Taxes:   classifies bank outflows as excise / payroll-tax / federal via
//          configurable keywords (cashflow.taxes), bucketed by month.
// Payroll: detects checks whose number falls in any of the user-saved ranges
//          (cashflow.payroll.getCheckRanges). Settings UI lives in Account.
// Census:  stub for upcoming county-level economic data.

// Display label for a bank/card transaction row: raw ACH descriptors carry
// NACHA fields ("… NAME:ADP WAGE PAY ORIG ID:…"); show only the NAME: value.
function txLabel(tx) {
  const clean = window.kanjo?.cashflow?.util?.cleanTxnName || ((s) => s);
  return clean(tx.merchantName || tx.description) || '—';
}

function useTrailingTxs(days = 365, { includeCardCharges = false } = {}) {
  const [st, setSt] = React.useState({ loading: true, txs: [], currency: 'USD', locale: 'en-US' });
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const claims = await window.kanjo?.auth?.currentClaims?.().catch(() => null);
        if (!claims) { if (!cancelled) setSt({ loading: false, txs: [] }); return; }
        const db = window.kanjo.db;
        const today = localToday();
        const cutoff = new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);
        const txsAll = await db.plaidTransactions
          .where('[userId+date]').between([claims.sub, cutoff], [claims.sub, today], true, true)
          .toArray();
        // Default feed (taxes / payroll): DEPOSITORY only — their descriptor
        // matching (check ranges, "ADP TAX"-style keywords) could false-positive
        // on card charges, and wages/taxes aren't paid by card.
        // includeCardCharges (rent & utilities): card CHARGES are spend on the
        // day charged (owner card model), so a utility bill on the card counts;
        // only the bill-payment legs (LOAN_PAYMENTS, both sides) are dropped —
        // that spend is already counted as the charges. Same basis as the
        // Sankey's cost rollup, so the two stay reconciled.
        const acctLib = window.kanjo?.cashflow?.accounts;
        const creditSet = acctLib ? await acctLib.creditAccountIds(claims.sub) : new Set();
        let txs = !acctLib ? txsAll
          : includeCardCharges ? txsAll.filter((t) => !acctLib.isCardPayment(t))
          : acctLib.withoutCreditTxs(txsAll, creditSet);
        // Stamp each card charge — and each explicitly-categorized bank
        // outflow (Today's Recent-activity picker) — with the operator's
        // EXPLICIT category (_spendCat). The summarizers treat it as
        // authoritative both ways, so a categorized transaction lands in the
        // right report and a keyword match the operator rerouted stops
        // counting here. Stamped on BOTH feeds: the depository-only feed
        // (taxes/payroll) needs it so a bank row ruled "Taxes" counts there
        // and one ruled elsewhere stops counting as a payroll run/fee.
        try {
          const sc = window.kanjo.cashflow.spend;
          txs = sc.stampCardCategories(txs, creditSet, await sc.loadExplicitContext(claims.sub));
        } catch (e) { console.warn('[reports] spend-category stamp skipped', e); }
        const profile = await db.userProfile.get(claims.sub).catch(() => null);
        if (!cancelled) setSt({
          loading: false, txs,
          currency: profile?.currency || 'USD',
          locale: profile?.locale || 'en-US',
        });
      } catch (e) {
        if (!cancelled) setSt({ loading: false, txs: [], error: String(e?.message || e) });
      }
    })();
    return () => { cancelled = true; };
  }, [days]);
  return st;
}

const ymLabel = (ym, locale) => {
  if (!ym) return '—';
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, (m || 1) - 1, 1).toLocaleDateString(locale || 'en-US', { month: 'long', year: 'numeric' });
};

// ── Timeframe filter shared by Taxes + Payroll reports ────────────────
// '12mo'    → trailing 365 days (the default; matches the cached pull)
// 'year'    → from Jan 1 of the current calendar year
// 'quarter' → from the first day of the current calendar quarter (Q1=Jan,
//             Q2=Apr, Q3=Jul, Q4=Oct)
const TIMEFRAMES = ['quarter', 'year', '12mo'];

function timeframeCutoffIso(tf, today = new Date()) {
  // Build the local calendar date directly — toISOString() on a local-midnight
  // Date shifts a day back on UTC+ devices (Jan 1 → Dec 31).
  if (tf === 'year') {
    return `${today.getFullYear()}-01-01`;
  }
  if (tf === 'quarter') {
    const q = Math.floor(today.getMonth() / 3);
    return `${today.getFullYear()}-${String(q * 3 + 1).padStart(2, '0')}-01`;
  }
  // 12mo (default) — match the data pull window.
  return new Date(today.getTime() - 365 * 86400000).toISOString().slice(0, 10);
}

function filterByTimeframe(txs, tf) {
  if (tf === '12mo') return txs;  // already trimmed by useTrailingTxs(365)
  const cutoff = timeframeCutoffIso(tf);
  return (txs || []).filter((t) => (t.date || '') >= cutoff);
}

function timeframeLabel(tf, today = new Date(), locale) {
  if (tf === 'year') {
    return t('reports.tf.year', 'This year') + ' · ' + today.getFullYear();
  }
  if (tf === 'quarter') {
    const q = Math.floor(today.getMonth() / 3) + 1;
    return t('reports.tf.quarter', 'This quarter') + ' · Q' + q + ' ' + today.getFullYear();
  }
  return t('reports.tf.12mo', 'Last 12 months');
}

function TimeframeTabs({ value, onChange }) {
  const opts = [
    ['quarter', t('reports.tf.quarter', 'This quarter')],
    ['year',    t('reports.tf.year', 'This year')],
    ['12mo',    t('reports.tf.12mo', 'Last 12 months')],
  ];
  return (
    <div role="tablist" aria-label="Timeframe" style={{
      display: 'flex', padding: 3, gap: 3,
      background: 'var(--surface-3,#f0f0f3)', borderRadius: 8, marginBottom: 14,
    }}>
      {opts.map(([id, label]) => {
        const on = value === id;
        return (
          <button key={id} role="tab" aria-selected={on} onClick={() => onChange(id)}
            style={{
              flex: 1, padding: '7px 10px', borderRadius: 6, border: 'none', cursor: 'pointer',
              fontSize: 12.5, fontWeight: 600,
              background: on ? 'var(--surface,#fff)' : 'transparent',
              color: on ? 'var(--ink)' : 'var(--ink-3)',
              boxShadow: on ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
            }}>{label}</button>
        );
      })}
    </div>
  );
}

// Build a fixed-spine 12-month series from a tx list with a per-month
// classifier — used by the Taxes chart. Each entry is { ym, label,
// total } where label is the short month name.
function buildMonthly12(txs, amountOf, today = new Date(), locale) {
  const months = [];
  for (let i = 11; i >= 0; i--) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    months.push({
      ym,
      label: d.toLocaleDateString(locale || 'en-US', { month: 'short', timeZone: 'UTC' }),
      total: 0,
    });
  }
  const byYm = new Map(months.map((m) => [m.ym, m]));
  for (const tx of (txs || [])) {
    const ym = (tx.date || '').slice(0, 7);
    const m = byYm.get(ym); if (!m) continue;
    m.total += amountOf(tx) || 0;
  }
  return months;
}
const fmtDateMdy = (iso, locale) => {
  if (!iso) return '—';
  return new Date(iso + 'T00:00:00Z').toLocaleDateString(locale || 'en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

// ─── Taxes ────────────────────────────────────────────────────────────────
function TaxReport({ dark = false, persona = 'owner' }) {
  useT();
  const nav = React.useContext(KanjoNav);
  const { loading, txs, currency, locale, error } = useTrailingTxs(365);
  const [keywords, setKeywords] = React.useState(null);
  const [tf, setTf] = React.useState('12mo');
  const [cfgOpen, setCfgOpen] = React.useState(false);
  const loadKeywords = () => window.kanjo?.cashflow?.taxes?.getKeywords?.().then(setKeywords).catch(() => setKeywords(null));
  React.useEffect(() => { loadKeywords(); }, []);
  const txsInTf = React.useMemo(() => filterByTimeframe(txs, tf), [txs, tf]);
  const sum = React.useMemo(() => {
    if (!keywords) return null;
    return window.kanjo.cashflow.taxes.summarize(txsInTf, keywords);
  }, [txsInTf, keywords]);
  // 12-month chart series — always full year, regardless of timeframe,
  // so the chart provides constant context. Built from the full txs +
  // the taxes classifier; we re-summarize to know which txs counted.
  const chartMonths = React.useMemo(() => {
    if (!keywords) return null;
    const sumAll = window.kanjo.cashflow.taxes.summarize(txs, keywords);
    const countedIds = new Set();
    for (const m of sumAll.months || []) {
      for (const r of m.rows || []) countedIds.add(r.plaidTxId);
    }
    const taxTxs = (txs || []).filter((tx) => countedIds.has(tx.plaidTxId));
    return buildMonthly12(taxTxs, (tx) => tx.amountCents || 0, new Date(), locale);
  }, [txs, keywords, locale]);
  // Setup state only when there are no keywords AND nothing was explicitly
  // categorized as Taxes from the Spending/Today picker — an operator who
  // routed e.g. a bare "ADP" IRS remittance here must see it, keywords or not.
  const noKeywords = keywords ? !window.kanjo.cashflow.taxes.hasAnyKeyword(keywords) && !(sum && sum.totals.total !== 0) : false;
  const headlineLabel = timeframeLabel(tf, new Date(), locale);

  const M = (c) => <KMoney cents={c} currency={currency} locale={locale} style={{ fontSize: 13, fontWeight: 600 }}/>;
  const xl = (c, tone) => <KMoney cents={c} currency={currency} locale={locale} size="xl" tone={tone}/>;

  return (
    <KShell dark={dark} tab="reports" persona={persona}>
      <div style={{ height: '100%', overflowY: 'auto', paddingBottom: 24 }}>
        <KHeader back title={t('reports.taxes', 'Taxes')} subtitle={t('reports.taxesSub', 'Detected from bank activity')}
          right={<GearButton label={t('account.taxKeywords', 'Tax keywords')} onClick={() => setCfgOpen(true)} />} />
        <Sheet open={cfgOpen} onClose={() => { setCfgOpen(false); loadKeywords(); }} title={t('account.taxKeywords', 'Tax keywords')}>
          <TaxKeywordSettings />
        </Sheet>
        <div style={{ padding: '0 20px' }}>
          {error && <ErrorBanner>{error}</ErrorBanner>}
          {(loading || !sum) && !error && <SkeletonCard height={120}/>}
          {sum && !loading && noKeywords && (
            <div className="card" style={{ padding: 20 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>
                {t('reports.taxesSetupTitle', 'Tell Kanjo what your tax payments look like')}
              </div>
              <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.55, marginTop: 8 }}>
                {t('reports.taxesSetupBody', 'Tap the gear above and add the merchant names or ACH descriptors that appear on your tax-payment transactions (e.g. "ADP Tax", "WA DEPT REVENUE", "EFTPS"). Kanjo only counts what you tell it to — no built-in guesses.')}
              </div>
              <button onClick={() => setCfgOpen(true)} className="btn primary" style={{ marginTop: 14 }}>
                {t('reports.taxesSetupOpen', 'Add tax keywords')}
              </button>
            </div>
          )}
          {sum && !loading && !noKeywords && (
            <>
              <TimeframeTabs value={tf} onChange={setTf} />

              {/* Headline scoped to the selected timeframe */}
              <div className="card" style={{ padding: 22, marginBottom: 14 }}>
                <div className="label">{t('reports.taxesScoped', 'Tax payments · {tf}').replace('{tf}', headlineLabel)}</div>
                <div style={{ marginTop: 6 }}>{xl(sum.totals.total, 'danger')}</div>
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--rule)',
                  display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                  <div>
                    <div className="label">{t('reports.taxExcise', 'Excise / sales')}</div>
                    <div style={{ marginTop: 4 }}>{M(sum.totals.excise)}</div>
                  </div>
                  <div>
                    <div className="label">{t('reports.taxPayroll', 'Payroll')}</div>
                    <div style={{ marginTop: 4 }}>{M(sum.totals.payroll)}</div>
                  </div>
                  <div>
                    <div className="label">{t('reports.taxFederal', 'Federal')}</div>
                    <div style={{ marginTop: 4 }}>{M(sum.totals.federal)}</div>
                  </div>
                </div>
              </div>

              {/* 12-month chart — always full year for context */}
              {chartMonths && chartMonths.some((m) => m.total > 0) && (() => {
                const maxMonth = Math.max(1, ...chartMonths.map((m) => m.total));
                const thisYm = (() => {
                  const d = new Date();
                  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                })();
                return (
                  <div className="card" style={{ padding: 16, marginBottom: 14 }}>
                    <div className="label" style={{ marginBottom: 10 }}>{t('reports.taxesMonthly', 'Monthly trend · last 12 months')}</div>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', height: 96 }}>
                      {chartMonths.map((m) => {
                        const h = m.total > 0 ? Math.max(3, Math.round((m.total / maxMonth) * 88)) : 2;
                        const isCurrent = m.ym === thisYm;
                        return (
                          <div key={m.ym}
                            title={`${m.label} — ${(m.total / 100).toLocaleString(locale || 'en-US', { style: 'currency', currency })}`}
                            style={{ flex: 1, height: h, borderRadius: 3,
                              background: m.total > 0 ? 'var(--danger,#d2563f)' : 'var(--surface-3)',
                              opacity: isCurrent ? 0.55 : 1 }}/>
                        );
                      })}
                    </div>
                    <div style={{ display: 'flex', gap: 4, marginTop: 6, fontSize: 11, color: 'var(--ink-3)' }}>
                      {chartMonths.map((m) => (
                        <div key={m.ym} style={{ flex: 1, textAlign: 'center' }}>{m.label}</div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {sum.months.length === 0 ? (
                <Empty title={t('reports.taxesNone', 'No tax payments detected')}
                  body={t('reports.taxesNoneBody', 'Excise (around the 25th of each month), payroll-tax and federal payments show up here once they land in your linked bank account. Connect a bank in Account to enable detection.')} />
              ) : (
                <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  <div className="label" style={{ padding: '14px 16px 6px' }}>{t('reports.taxesByMonth', 'By month')}</div>
                  {sum.months.map((m, i) => (
                    <details key={m.ym} style={{ borderTop: '1px solid var(--rule)' }}>
                      <summary style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '12px 16px', cursor: 'pointer', listStyle: 'none' }}>
                        <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink)' }}>{ymLabel(m.ym, locale)}</span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <KMoney cents={m.total} currency={currency} locale={locale} tone="danger" style={{ fontSize: 14, fontWeight: 700 }}/>
                          <Chev size={14}/>
                        </span>
                      </summary>
                      <div style={{ padding: '4px 16px 12px' }}>
                        {m.rows.map((tx) => (
                          <div key={tx.plaidTxId}
                            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                              padding: '6px 0', fontSize: 12.5, color: 'var(--ink-2)' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                              <span style={{ width: 6, height: 6, borderRadius: 3,
                                background: tx._bucket === 'payroll' ? 'var(--brand,#5b8def)'
                                          : tx._bucket === 'federal' ? 'var(--warning,#9a6700)'
                                          : 'var(--ink-3)' }}/>
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {fmtDateMdy(tx.date, locale)} · {txLabel(tx)}
                              </span>
                            </span>
                            <KMoney cents={tx.amountCents} currency={currency} locale={locale} tone="danger"
                              style={{ fontSize: 12.5, fontWeight: 600 }}/>
                          </div>
                        ))}
                      </div>
                    </details>
                  ))}
                </div>
              )}
              <div style={{ fontSize: 11.5, color: 'var(--on-dim)', marginTop: 12, lineHeight: 1.5 }}>
                {t('reports.taxesFootnote', 'Detected by matching the keywords you saved (tap the gear above) against bank descriptors. Add or remove keywords if you see misses or false positives.')}
              </div>
            </>
          )}
        </div>
      </div>
    </KShell>
  );
}

// ─── Payroll ──────────────────────────────────────────────────────────────
// Local BLS Leisure & Hospitality wage/labor benchmark for the Payroll report.
// Self-contained: loads the business ZIP from the profile, fetches /v1/wages,
// and renders earnings + weekly hours + employment with 12-month % change.
// Earnings/hours come from BLS at the state level for most areas (metro often
// unpublished); employment is per-metro. Public-domain data — attribution +
// non-endorsement disclaimer shown per BLS Terms of Use.
// Persist the labor-market tile's open/closed state (allow-listed key, direct
// localStorage — mirrors the theme + recent-window prefs). Collapsed by default:
// it's reference context, not something to re-read every visit.
const WAGE_OPEN_KEY = 'kanjoWageCardOpen';
function readWageOpen() {
  try { return localStorage.getItem(WAGE_OPEN_KEY) === '1'; } catch { return false; }
}

function WageBenchmarkCard() {
  useT();
  const [st, setSt] = React.useState({ phase: 'loading' }); // loading|ok|no-zip|none
  const [open, setOpenState] = React.useState(readWageOpen);
  const setOpen = (v) => { setOpenState(v); try { localStorage.setItem(WAGE_OPEN_KEY, v ? '1' : '0'); } catch { /* private mode */ } };
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = await window.kanjo?.scale?.profile?.getProfile?.().catch(() => null);
        const zip = (p && p.zip) ? String(p.zip).trim() : '';
        if (!/^\d{5}$/.test(zip)) { if (!cancelled) setSt({ phase: 'no-zip' }); return; }
        const data = await window.kanjo.scale.wages.fetchWages({ zip });
        if (!cancelled) setSt({ phase: 'ok', data });
      } catch (e) {
        if (!cancelled) setSt({ phase: 'none' }); // unavailable / error → hide quietly
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (st.phase === 'none') return null;
  if (st.phase === 'loading') return <SkeletonCard height={120}/>;
  if (st.phase === 'no-zip') {
    return (
      <div className="card" style={{ padding: 16, marginBottom: 14 }}>
        <div className="label">{t('reports.blsTitle', 'Local labor market · Leisure & Hospitality')}</div>
        <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 6, lineHeight: 1.5 }}>
          {t('reports.blsNoZip', 'Add your business ZIP in Account → Business profile to see local wage and hiring benchmarks.')}
        </div>
      </div>
    );
  }
  const d = st.data;
  const metroName = (d.areaName || '').split(',')[0].trim(); // "Seattle-Bellevue-Kent, WA Metro Div" → "Seattle-Bellevue-Kent"
  const stateName = d.stateName || '';
  // Label every metric with the geography it ACTUALLY came from, so a statewide
  // wage can't be misread as a metro figure: BLS publishes L&H earnings/hours
  // only statewide, while employment is per-metro.
  const scope = (level) => level === 'metro'
    ? (metroName || t('reports.blsMetro', 'metro area'))
    : (stateName ? `${stateName} · statewide` : t('reports.blsStatewide', 'statewide'));
  const pct = (v) => v == null ? '—' : `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v).toFixed(1)}%`;
  const tone = (v) => v == null ? 'var(--ink-3)' : v > 0 ? 'var(--positive,#2e9e5b)' : v < 0 ? 'var(--danger,#d2563f)' : 'var(--ink-3)';
  // One row per metric, so the tile reads as an aligned table:
  // metric · value · 12-mo change · the geography it came from.
  const rows = [
    d.earnings && { label: t('reports.blsEarnings', 'Avg hourly wage'), value: `$${d.earnings.usd.toFixed(2)}`, yoy: d.earnings.yoyPct, loc: scope(d.earnings.level) },
    d.weeklyHours && { label: t('reports.blsHours', 'Avg weekly hours'), value: d.weeklyHours.hours.toFixed(1), yoy: d.weeklyHours.yoyPct, loc: scope(d.weeklyHours.level) },
    d.employment && { label: t('reports.blsJobs', 'L&H jobs'), value: `${d.employment.thousands.toLocaleString()}k`, yoy: d.employment.yoyPct, loc: scope(d.employment.level) },
  ].filter(Boolean);
  const thStyle = { textAlign: 'left', fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--ink-3)', padding: '0 8px 6px 0', whiteSpace: 'nowrap' };
  const tdStyle = { fontSize: 13, color: 'var(--ink)', padding: '7px 8px 7px 0', borderTop: '1px solid var(--rule)', verticalAlign: 'top', whiteSpace: 'nowrap' };
  // Employment trend → plain-language hiring signal.
  const empYoy = d.employment?.yoyPct;
  const hiring = empYoy == null ? null
    : empYoy > 0.5 ? t('reports.blsHiringUp', 'jobs rising — hiring market tightening, expect wage pressure')
    : empYoy < -0.5 ? t('reports.blsHiringDown', 'jobs falling — hiring market loosening')
    : t('reports.blsHiringFlat', 'roughly flat hiring');

  // Collapsed peek: the headline wage, so the tile still says something at a
  // glance without being expanded.
  const peek = d.earnings ? `$${d.earnings.usd.toFixed(2)}/hr` : null;

  return (
    <div className="card" style={{ padding: 16, marginBottom: 14 }}>
      <button onClick={() => setOpen(!open)} aria-expanded={open}
        style={{ width: '100%', background: 'transparent', border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
          padding: 0, textAlign: 'left' }}>
        <span className="label" style={{ margin: 0 }}>{t('reports.blsTitle', 'Local labor market · Leisure & Hospitality')}</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          {!open && peek && <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink-2)' }}>{peek}</span>}
          <span style={{ display: 'inline-flex', color: 'var(--ink-3)', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .12s' }}>
            <Chev size={14}/>
          </span>
        </span>
      </button>
      {open && (
        <div style={{ marginTop: 10 }}>
          {d.asOf && <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 0, marginBottom: 10 }}>{t('reports.blsAsOf', 'U.S. Bureau of Labor Statistics · as of {m}').replace('{m}', d.asOf)}</div>}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>{t('reports.blsColMetric', 'Metric')}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('reports.blsColValue', 'Value')}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('reports.blsColChange', 'vs 12 mo')}</th>
                  <th style={thStyle}>{t('reports.blsColArea', 'Location')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.label}>
                    <td style={tdStyle}>{r.label}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700 }}>{r.value}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: tone(r.yoy) }}>{pct(r.yoy)}</td>
                    <td style={{ ...tdStyle, fontSize: 11.5, color: 'var(--ink-3)', whiteSpace: 'normal' }}>{r.loc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {hiring && <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 10, lineHeight: 1.45 }}>{hiring}</div>}
          <div style={{ fontSize: 10.5, color: 'var(--ink-3)', marginTop: 10, lineHeight: 1.5 }}>
            {t('reports.blsSource', 'Source: U.S. Bureau of Labor Statistics — State & Area Employment, Hours & Earnings (Leisure & Hospitality), retrieved {date}. BLS does not vouch for analyses or interpretations derived from its data.')
              .replace('{date}', (d.fetchedAt || '').slice(0, 10))}
          </div>
        </div>
      )}
    </div>
  );
}

function PayrollReport({ dark = false, persona = 'owner' }) {
  useT();
  const { loading, txs, currency, locale, error } = useTrailingTxs(365);
  const [ranges, setRanges] = React.useState(null);
  const [taxKw, setTaxKw] = React.useState(null);
  const [tf, setTf] = React.useState('12mo');
  const [cfgOpen, setCfgOpen] = React.useState(false);
  const loadRanges = () => window.kanjo?.cashflow?.payroll?.getCheckRanges?.().then(setRanges).catch(() => setRanges([]));
  React.useEffect(() => {
    loadRanges();
    window.kanjo?.cashflow?.taxes?.getKeywords?.().then(setTaxKw).catch(() => setTaxKw(null));
  }, []);
  // Always summarize the full 365-day window for the chart; build a
  // timeframe-scoped sum from the filtered tx slice for the headline +
  // activity list.
  const sumChecks = React.useMemo(() => {
    if (!ranges) return null;
    return window.kanjo.cashflow.payroll.summarizeChecks(txs, ranges);
  }, [txs, ranges]);
  const sumAch = React.useMemo(() => {
    if (!taxKw) return null;
    return window.kanjo.cashflow.payroll.summarizeAch(txs, taxKw);
  }, [txs, taxKw]);
  const cutoff = React.useMemo(() => timeframeCutoffIso(tf), [tf]);
  // In-timeframe slice of the summarized check + ACH arrays.
  const inTf = React.useCallback((arr) => (arr || []).filter((tx) => (tx.date || '') >= cutoff), [cutoff]);

  // Build a combined per-month series across the last 12 ISO months. Checks +
  // ACH runs + fees count as payroll spend; 401k is rolled separately (YTD).
  const monthly = React.useMemo(() => {
    if (!sumChecks || !sumAch) return null;
    const today = new Date();
    const months = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      months.push({ ym, label: d.toLocaleDateString(locale || 'en-US', { month: 'short', timeZone: 'UTC' }),
        checks: 0, runs: 0, fees: 0, total: 0 });
    }
    const byYm = new Map(months.map((m) => [m.ym, m]));
    const bucket = (tx, kind) => {
      const ym = (tx.date || '').slice(0, 7);
      const m = byYm.get(ym); if (!m) return;
      m[kind] += tx.amountCents || 0;
      m.total += tx.amountCents || 0;
    };
    for (const tx of sumChecks.checks) bucket(tx, 'checks');
    for (const tx of sumAch.runs)      bucket(tx, 'runs');
    for (const tx of sumAch.fees)      bucket(tx, 'fees');
    // YTD 401k — current calendar year only.
    const yStart = `${today.getFullYear()}-01`;
    let k401YtdCents = 0;
    const k401Ytd = [];
    for (const tx of sumAch.k401) {
      if ((tx.date || '') >= `${today.getFullYear()}-01-01`) {
        k401Ytd.push(tx); k401YtdCents += tx.amountCents || 0;
      }
    }
    const thisYm = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
    return { months, thisYm, k401Ytd, k401YtdCents,
      yearLabel: String(today.getFullYear()) };
  }, [sumChecks, sumAch, locale]);

  // Timeframe-scoped totals + activity (replaces the old this-month-only
  // headline). Activity is sorted desc by date and tagged by kind.
  const scoped = React.useMemo(() => {
    if (!sumChecks || !sumAch) return null;
    const checks = inTf(sumChecks.checks);
    const runs   = inTf(sumAch.runs);
    const fees   = inTf(sumAch.fees);
    const totals = { checks: 0, runs: 0, fees: 0, total: 0 };
    for (const tx of checks) { totals.checks += tx.amountCents || 0; totals.total += tx.amountCents || 0; }
    for (const tx of runs)   { totals.runs   += tx.amountCents || 0; totals.total += tx.amountCents || 0; }
    for (const tx of fees)   { totals.fees   += tx.amountCents || 0; totals.total += tx.amountCents || 0; }
    const activity = [
      ...checks.map((tx) => ({ tx, kind: 'check' })),
      ...runs.map((tx) => ({ tx, kind: 'run' })),
      ...fees.map((tx) => ({ tx, kind: 'fee' })),
    ].sort((a, b) => (b.tx.date || '').localeCompare(a.tx.date || ''));
    return { totals, activity };
  }, [sumChecks, sumAch, inTf]);
  const headlineLabel = timeframeLabel(tf, new Date(), locale);

  // Sales over the SAME timeframe, computed the SAME way as the COGS report
  // (revenue.cogs.calc → settled net sales), so labor-cost % lines up with COGS %.
  const [salesCents, setSalesCents] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const toIso = new Date().toISOString().slice(0, 10);
        // Settled denominator for the month-aligned timeframes (year/quarter),
        // matching the COGS report; the rolling 12mo window stays deposit-dated.
        const r = await window.kanjo.revenue.cogs.calc({ from: cutoff, to: toIso, includeWaste: false, settled: tf !== '12mo' });
        if (!cancelled) setSalesCents(r?.netSalesCents ?? 0);
      } catch { if (!cancelled) setSalesCents(0); }
    })();
    return () => { cancelled = true; };
  }, [cutoff, tf]);
  // Employee wages = checks + ACH runs (fees and 401(k) excluded). Labor cost % =
  // wages ÷ sales — the gauge denominator matches COGS's.
  const wagesCents = scoped ? (scoped.totals.checks + scoped.totals.runs) : 0;
  const laborPct = (salesCents && salesCents > 0) ? wagesCents / salesCents : null;

  const nav = React.useContext(KanjoNav);

  return (
    <KShell dark={dark} tab="reports" persona={persona}>
      <div style={{ height: '100%', overflowY: 'auto', paddingBottom: 24 }}>
        <KHeader back title={t('reports.payroll', 'Payroll')} subtitle={t('reports.payrollSub', 'Detected from check numbers')}
          right={<GearButton label={t('account.payrollRanges', 'Payroll check ranges')} onClick={() => setCfgOpen(true)} />} />
        <Sheet open={cfgOpen} onClose={() => { setCfgOpen(false); loadRanges(); }} title={t('account.payrollRanges', 'Payroll check ranges')}>
          <PayrollRangeSettings />
        </Sheet>
        <div style={{ padding: '0 20px' }}>
          {/* Local BLS wage/hiring benchmark — independent of the operator's own
              payroll detection, so it shows regardless of check-range setup. */}
          <WageBenchmarkCard />
          {error && <ErrorBanner>{error}</ErrorBanner>}
          {(loading || !sumChecks || !sumAch || !monthly) && !error && <SkeletonCard height={120}/>}
          {sumChecks && sumAch && monthly && scoped && !loading && (() => {
            const hasAnyPayroll = sumChecks.checks.length + sumAch.runs.length + sumAch.fees.length > 0;
            const noRanges = !ranges || ranges.length === 0;
            const maxMonth = Math.max(1, ...monthly.months.map((m) => m.total));
            return (
              <>
                {/* Empty state ONLY when there are no ranges AND no ACH-based
                    payroll detected — an ADP-only operator never needs ranges. */}
                {noRanges && !hasAnyPayroll && sumAch.k401.length === 0 && (
                  <div className="card" style={{ padding: 20 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>
                      {t('reports.payrollSetupTitle', 'Set up payroll detection')}
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.55, marginTop: 8 }}>
                      {t('reports.payrollSetupBody', "Use a dedicated checkbook for payroll (e.g. #50000-#50999) and tell Kanjo which check numbers belong to it. Any bank transaction matching that range will appear here as payroll. You can add multiple ranges — one per checkbook.")}
                    </div>
                    <button onClick={() => setCfgOpen(true)} className="btn primary"
                      style={{ marginTop: 14 }}>
                      {t('reports.payrollOpenSettings', 'Add a check range')}
                    </button>
                  </div>
                )}

                {hasAnyPayroll && <TimeframeTabs value={tf} onChange={setTf} />}

                {/* Timeframe-scoped headline */}
                {hasAnyPayroll && (
                  <div className="card" style={{ padding: 22, marginBottom: 14 }}>
                    <div className="label">{t('reports.payrollScoped', 'Payroll · {tf}').replace('{tf}', headlineLabel)}</div>
                    <div style={{ marginTop: 6 }}>
                      <KMoney cents={scoped.totals.total} currency={currency} locale={locale} size="xl" tone="danger"/>
                    </div>
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--rule)',
                      display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                      <div>
                        <div className="label">{t('reports.payrollChecks', 'Checks')}</div>
                        <div style={{ marginTop: 4 }}>
                          <KMoney cents={scoped.totals.checks} currency={currency} locale={locale} style={{ fontSize: 13, fontWeight: 600 }}/>
                        </div>
                      </div>
                      <div>
                        <div className="label">{t('reports.payrollRuns', 'ACH runs')}</div>
                        <div style={{ marginTop: 4 }}>
                          <KMoney cents={scoped.totals.runs} currency={currency} locale={locale} style={{ fontSize: 13, fontWeight: 600 }}/>
                        </div>
                      </div>
                      <div>
                        <div className="label">{t('reports.payrollFees', 'Fees')}</div>
                        <div style={{ marginTop: 4 }}>
                          <KMoney cents={scoped.totals.fees} currency={currency} locale={locale} style={{ fontSize: 13, fontWeight: 600 }}/>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Labor cost gauge — wages ÷ sales, same arc gauge + sales basis as COGS. */}
                {hasAnyPayroll && (
                  <div className="card" style={{ padding: 18, marginBottom: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                      <div className="label">{t('reports.laborCost', 'Labor cost')} · {headlineLabel}</div>
                      <div className="mono" style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-2)' }}>
                        {laborPct != null ? `${(laborPct * 100).toFixed(1)}%` : '—'}
                      </div>
                    </div>
                    <FoodCostGauge value={laborPct != null ? laborPct * 100 : null} target={30} label="Labor cost" />
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
                      <span>{t('reports.wages', 'Wages')} <KMoney cents={wagesCents} currency={currency} locale={locale} style={{ fontSize: 12 }}/></span>
                      <span>{t('today.salesDeposits', 'Sales (deposits)')} {salesCents != null
                        ? <KMoney cents={salesCents} currency={currency} locale={locale} style={{ fontSize: 12 }}/>
                        : '—'}</span>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 6, lineHeight: 1.4 }}>
                      {t('reports.laborGaugeHint', 'Employee wages (checks + ACH runs) ÷ sales — same sales as your COGS report. Fees and 401(k) excluded.')}
                    </div>
                  </div>
                )}

                {/* Per-month chart — last 12 months, current month faded. */}
                {hasAnyPayroll && (
                  <div className="card" style={{ padding: 16, marginBottom: 14 }}>
                    <div className="label" style={{ marginBottom: 10 }}>{t('reports.payrollMonthly', 'Monthly trend · last 12 months')}</div>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', height: 96 }}>
                      {monthly.months.map((m) => {
                        const h = m.total > 0 ? Math.max(3, Math.round((m.total / maxMonth) * 88)) : 2;
                        const isCurrent = m.ym === monthly.thisYm;
                        return (
                          <div key={m.ym} title={`${m.label} — ${(m.total / 100).toLocaleString(locale || 'en-US', { style: 'currency', currency })}`}
                            style={{ flex: 1, height: h, borderRadius: 3,
                              background: m.total > 0 ? 'var(--danger,#d2563f)' : 'var(--surface-3)',
                              opacity: isCurrent ? 0.55 : 1 }}/>
                        );
                      })}
                    </div>
                    <div style={{ display: 'flex', gap: 4, marginTop: 6, fontSize: 11, color: 'var(--ink-3)' }}>
                      {monthly.months.map((m) => (
                        <div key={m.ym} style={{ flex: 1, textAlign: 'center' }}>{m.label}</div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Timeframe-scoped activity list */}
                {hasAnyPayroll && (
                  <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 14 }}>
                    <div className="label" style={{ padding: '14px 16px 6px' }}>
                      {t('reports.payrollActivityScoped', 'Activity · {tf}').replace('{tf}', headlineLabel)}
                    </div>
                    {scoped.activity.length === 0 ? (
                      <div style={{ padding: '12px 16px', fontSize: 12.5, color: 'var(--ink-3)' }}>
                        {t('reports.payrollNoActivityTf', 'No payroll activity in this timeframe.')}
                      </div>
                    ) : scoped.activity.map(({ tx, kind }) => (
                      <div key={tx.plaidTxId}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '12px 16px', borderTop: '1px solid var(--rule)' }}>
                        <span style={{ minWidth: 0, paddingRight: 12 }}>
                          <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink)' }}>
                            {kind === 'check' ? `#${tx._checkNumber}` : txLabel(tx)}
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
                            {fmtDateMdy(tx.date, locale)}
                            {' · '}
                            <span style={{ color: kind === 'fee' ? 'var(--warning,#9a6700)' : 'var(--ink-3)' }}>
                              {kind === 'check' ? t('reports.payrollCheck', 'check')
                                : kind === 'fee' ? t('reports.payrollFee', 'payroll fee')
                                : t('reports.payrollRun', 'ACH run')}
                            </span>
                          </div>
                        </span>
                        <KMoney cents={tx.amountCents} currency={currency} locale={locale} tone="danger"
                          style={{ fontSize: 14, fontWeight: 700 }}/>
                      </div>
                    ))}
                  </div>
                )}

                {/* 401k YTD — only when 401k transfers are detected. Year-to-date
                    for the current calendar year, framed as transfers (not an
                    expense to the business). */}
                {monthly.k401Ytd.length > 0 && (
                  <div className="card" style={{ padding: 18, marginBottom: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                      <div className="label">
                        {t('reports.retirementYtd', '401k · YTD {year}').replace('{year}', monthly.yearLabel)}
                      </div>
                      <KMoney cents={monthly.k401YtdCents} currency={currency} locale={locale}
                        style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink)' }}/>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4, marginBottom: 10 }}>
                      {t('reports.retirementBody', "Not a business expense — money transferred to your employees' retirement plan.")}
                    </div>
                    {monthly.k401Ytd.map((tx) => (
                      <div key={tx.plaidTxId}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '8px 0', borderTop: '1px solid var(--hairline)', fontSize: 13 }}>
                        <span style={{ minWidth: 0, paddingRight: 12, color: 'var(--ink-2)' }}>
                          {fmtDateMdy(tx.date, locale)} · {txLabel(tx)}
                        </span>
                        <KMoney cents={tx.amountCents} currency={currency} locale={locale}
                          style={{ fontSize: 13, fontWeight: 600 }}/>
                      </div>
                    ))}
                  </div>
                )}

                {!hasAnyPayroll && monthly.k401Ytd.length === 0 && !noRanges && (
                  <Empty title={t('reports.payrollNone', 'No payroll checks yet')}
                    body={t('reports.payrollNoneBody', 'Once a check in one of your saved ranges clears the bank, it shows up here. The trailing 365 days are scanned.')} />
                )}

                <div style={{ fontSize: 11.5, color: 'var(--on-dim)', marginTop: 12, lineHeight: 1.5 }}>
                  {t('reports.payrollFootnote2', 'Payroll is detected from check ranges (CHECK #…) and ACH descriptors (ADP, ADP FEE, GUSTO). 401k transfers are tracked separately because the money belongs to employees, not the business.')}
                </div>
              </>
            );
          })()}
        </div>
      </div>
    </KShell>
  );
}

// ─── Census Bureau Data ───────────────────────────────────────────────────
// Public-domain peer benchmarks (CBP) + trade-area demographics (ACS) for the
// operator's ZIP. Backend: GET /v1/market (unauthenticated; same endpoint the
// getkanjo.com landing widget uses). Spec: docs/CENSUS_BENCHMARKING_SPEC.md.
function CensusReport({ dark = false, persona = 'owner' }) {
  useT();
  const nav = React.useContext(KanjoNav);
  const { loading: txLoading, txs, currency, locale } = useTrailingTxs(365);
  const [profile, setProfile] = React.useState(null);
  const [payload, setPayload] = React.useState(null);
  const [phase, setPhase] = React.useState('loading'); // loading | no-zip | ok | not-benchmarkable | error
  const [errMsg, setErrMsg] = React.useState('');
  const [payrollRanges, setPayrollRanges] = React.useState(null);
  const [taxKw, setTaxKw] = React.useState(null);
  const [activeZip, setActiveZip] = React.useState(null); // tab selection for per-ZIP detail

  // Load profile, payroll config, and tax keywords in parallel.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [p, ranges, kw] = await Promise.all([
          window.kanjo?.scale?.profile?.getProfile?.().catch(() => null),
          window.kanjo?.cashflow?.payroll?.getCheckRanges?.().catch(() => []),
          window.kanjo?.cashflow?.taxes?.getKeywords?.().catch(() => null),
        ]);
        if (cancelled) return;
        setProfile(p || null);
        setPayrollRanges(ranges || []);
        setTaxKw(kw || null);
        const zip = (p && p.zip) ? String(p.zip).trim() : '';
        if (!/^\d{5}$/.test(zip)) { setPhase('no-zip'); return; }
        try {
          const data = await window.kanjo.scale.market.fetchMarket({ zip });
          if (cancelled) return;
          setPayload(data);
          setPhase('ok');
        } catch (e) {
          if (cancelled) return;
          if (e?.code === 'not_benchmarkable') { setPhase('not-benchmarkable'); return; }
          setErrMsg(String(e?.message || e));
          setPhase('error');
        }
      } catch (e) {
        if (!cancelled) { setErrMsg(String(e?.message || e)); setPhase('error'); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Annualized user payroll (TTM from check ranges + ACH runs).
  // Returns null when payroll isn't configured — UI hides the "you vs. them"
  // row and prompts the user to tag payroll.
  const userPayrollUsd = React.useMemo(() => {
    if (!payrollRanges || !taxKw) return null;
    if ((payrollRanges?.length || 0) === 0) return null;
    try {
      // TTM (useTrailingTxs covers 365d) → already an annualized number.
      // Excludes 401k (transfers, not an expense) per Payroll report convention.
      const checks = window.kanjo.cashflow.payroll.summarizeChecks(txs, payrollRanges);
      const ach    = window.kanjo.cashflow.payroll.summarizeAch(txs, taxKw);
      const cents  = (checks?.total || 0) + (ach?.runsCents || 0) + (ach?.feesCents || 0);
      return cents > 0 ? cents / 100 : null;
    } catch { return null; }
  }, [txs, payrollRanges, taxKw]);

  // "You vs. peers" compares the operator's TTM payroll against the
  // TRADE-AREA aggregate peer average (5 ZIPs), not just the primary ZIP.
  // That's the more honest comparison — peer payroll is suppressed at ZIP
  // scope by Census, so the aggregate is often the only available number.
  const comparison = React.useMemo(
    () => window.kanjo?.scale?.market?.peerPayrollComparison
      ? window.kanjo.scale.market.peerPayrollComparison(payload?.combined?.cbp, userPayrollUsd)
      : null,
    [payload, userPayrollUsd],
  );

  const usdFmt = React.useMemo(
    () => new Intl.NumberFormat(locale || 'en-US', { style: 'currency', currency: currency || 'USD', maximumFractionDigits: 0 }),
    [locale, currency],
  );
  const numFmt = React.useMemo(() => new Intl.NumberFormat(locale || 'en-US'), [locale]);
  const compact = (n) => Number.isFinite(n) ? usdFmt.format(Math.round(n / 1000) * 1000) : '—';

  return (
    <KShell dark={dark} tab="reports" persona={persona}>
      <div style={{ height: '100%', overflowY: 'auto', paddingBottom: 24 }}>
        <KHeader back
          title={t('reports.census', 'Your market')}
          subtitle={t('reports.censusSub', 'US Census peer benchmarks · public data, free')}
        />
        <div style={{ padding: '0 20px' }}>

          {(phase === 'loading' || (phase === 'ok' && txLoading)) && <SkeletonCard height={180}/>}

          {phase === 'no-zip' && (
            <div className="card" style={{ padding: 20 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>
                {t('reports.censusNoZipTitle', 'Add your business ZIP to see your market')}
              </div>
              <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.55, marginTop: 8 }}>
                {t('reports.censusNoZipBody', 'Census peer benchmarks are pulled for the ZIP your restaurant operates in. Add it once in Account → Business profile and this page fills in automatically.')}
              </div>
              <button onClick={() => nav.navigate('/account')} className="btn primary" style={{ marginTop: 14 }}>
                {t('reports.censusNoZipOpen', 'Open Account')}
              </button>
            </div>
          )}

          {phase === 'not-benchmarkable' && (
            <div className="card" style={{ padding: 20 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>
                {t('reports.censusSuppressedTitle', 'Census suppressed this ZIP')}
              </div>
              <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.55, marginTop: 8 }}>
                {t('reports.censusSuppressedBody', "Census doesn't publish restaurant statistics for ZIPs with very few establishments (to protect privacy). Try a neighboring ZIP, or wait for county-level fallback in a later update.")}
              </div>
            </div>
          )}

          {phase === 'error' && (
            <div className="card" style={{ padding: 20 }} role="alert">
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--danger)' }}>
                {t('reports.censusErrTitle', 'Could not load market data')}
              </div>
              <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 8 }}>{errMsg || '—'}</div>
            </div>
          )}

          {phase === 'ok' && payload && (() => {
            const zips = Array.isArray(payload.zips) ? payload.zips : [];
            const combined = payload.combined || null;
            const primaryZip = payload.primaryZip;
            const selectedZipCode = activeZip || primaryZip || zips[0]?.zip;
            const selectedZipData = zips.find((z) => z.zip === selectedZipCode) || zips[0] || null;

            // Render the three benchmark cards (competitive density,
            // peer cohort with optional you-vs-peers, demographics) for
            // any { cbp, acs } pair. Used twice: once for the trade-area
            // combined block, once for the selected per-ZIP tab.
            const renderCards = (cbp, acs, opts = {}) => {
              const fs   = cbp?.competitiveDensity?.fullService ?? null;
              const ls   = cbp?.competitiveDensity?.limitedService ?? null;
              const peer = cbp?.peerCohort || null;
              const densityLabel = opts.densityLabel || t('reports.censusDensity', 'Restaurants here');
              return (
                <>
                  {(fs !== null || ls !== null) && (
                    <div className="card" style={{ padding: 16, marginBottom: 12 }}>
                      <div className="label" style={{ marginBottom: 10 }}>{densityLabel}</div>
                      <div style={{ display: 'flex', gap: 16 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--ink)' }}>{fs != null ? numFmt.format(fs) : '—'}</div>
                          <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 2 }}>{t('reports.censusFullService', 'Full-service')}</div>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--ink)' }}>{ls != null ? numFmt.format(ls) : '—'}</div>
                          <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 2 }}>{t('reports.censusLimitedService', 'Limited-service')}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {peer && (
                    <div className="card" style={{ padding: 16, marginBottom: 12 }}>
                      <div className="label" style={{ marginBottom: 10 }}>
                        {t('reports.censusPeerCohort', 'Peers your size ({sz})').replace('{sz}', peer.sizeClassLabel || '')}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                        <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>{t('reports.censusEstablishments', 'Establishments')}</div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>{numFmt.format(peer.establishments)}</div>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                        <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>{t('reports.censusAvgPayroll', 'Avg. annual payroll')}</div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>{compact(peer.avgPayrollPerEstablishmentUsd)}</div>
                      </div>

                      {opts.showComparison && comparison && comparison.deltaPct != null && (
                        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--rule)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                            <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>{t('reports.censusYourPayroll', 'Your annualized payroll')}</div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>{compact(userPayrollUsd)}</div>
                          </div>
                          <div style={{ marginTop: 6, fontSize: 13, fontWeight: 600,
                            color: comparison.direction === 'below' ? 'var(--positive, #2e7d32)' : comparison.direction === 'above' ? 'var(--warn, #c62828)' : 'var(--ink-2)' }}>
                            {comparison.direction === 'on'
                              ? t('reports.censusOnPar', 'On par with peers')
                              : t('reports.censusDelta', '{pct}% {direction} same-size peers')
                                  .replace('{pct}', String(Math.abs(comparison.deltaPct).toFixed(0)))
                                  .replace('{direction}', comparison.direction === 'below'
                                    ? t('reports.censusDirBelow', 'below')
                                    : t('reports.censusDirAbove', 'above'))}
                          </div>
                        </div>
                      )}
                      {opts.showComparison && !comparison && (
                        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--rule)', fontSize: 12, color: 'var(--ink-2)', lineHeight: 1.5 }}>
                          {t('reports.censusNoPayroll', "Tag payroll transactions (in the Payroll report) to compare your annualized labor cost against this peer average.")}
                        </div>
                      )}
                    </div>
                  )}

                  {acs && (
                    <div className="card" style={{ padding: 16, marginBottom: 12 }}>
                      <div className="label" style={{ marginBottom: 10 }}>{t('reports.censusTradeArea', 'Trade-area demographics')}</div>
                      {acs.medianHouseholdIncomeUsd != null && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                          <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>{t('reports.censusMedianIncome', 'Median household income')}</div>
                          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>{compact(acs.medianHouseholdIncomeUsd)}</div>
                        </div>
                      )}
                      {acs.population != null && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                          <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>{t('reports.censusPopulation', 'Population')}</div>
                          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>{numFmt.format(acs.population)}</div>
                        </div>
                      )}
                      {acs.medianAge != null && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                          <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>{t('reports.censusMedianAge', 'Median age')}</div>
                          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>{acs.medianAge}</div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              );
            };

            return (
              <>
                {/* Trade-area header */}
                <div className="card" style={{ padding: 16, marginBottom: 12 }}>
                  <div style={{ fontSize: 12, color: 'var(--ink-2)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    {t('reports.censusTradeAreaLabel', 'Trade area · {n} ZIPs').replace('{n}', String(zips.length))}
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--ink)', marginTop: 4 }}>
                    {t('reports.censusTradeAreaCenter', 'ZIP {z} + {n} nearby').replace('{z}', primaryZip || '—').replace('{n}', String(Math.max(0, zips.length - 1)))}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--ink-3,#999)', marginTop: 6, lineHeight: 1.5 }}>
                    {t('reports.censusTradeAreaHelp', 'Aggregated across {z}.').replace('{z}', zips.map((x) => x.zip).join(', '))}
                  </div>
                </div>

                {/* Aggregation FIRST — the "how does my trade area look" view */}
                {combined && renderCards(combined.cbp, combined.acs, {
                  densityLabel: t('reports.censusDensityTradeArea', 'Restaurants in your trade area'),
                  showComparison: true,
                })}

                {/* Tab strip — primary + 4 neighbors */}
                {zips.length > 1 && (
                  <div role="tablist" aria-label={t('reports.censusZipTabs', 'Per-ZIP detail')}
                    style={{ display: 'flex', gap: 6, overflowX: 'auto', padding: '4px 0 12px', marginBottom: 6, scrollbarWidth: 'thin' }}>
                    {zips.map((z) => {
                      const isSel = z.zip === selectedZipCode;
                      return (
                        <button
                          key={z.zip}
                          role="tab"
                          aria-selected={isSel}
                          onClick={() => setActiveZip(z.zip)}
                          style={{
                            padding: '8px 14px',
                            fontSize: 13,
                            fontWeight: isSel ? 700 : 500,
                            borderRadius: 999,
                            border: isSel ? '1px solid var(--accent,#5b8def)' : '1px solid var(--hairline,#ddd)',
                            background: isSel ? 'var(--accent,#5b8def)' : 'var(--surface,#fff)',
                            color: isSel ? '#fff' : 'var(--ink,#111)',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {z.zip}{z.isPrimary ? ' ·' : ''}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Selected ZIP detail */}
                {selectedZipData && (
                  <>
                    <div style={{ fontSize: 12, color: 'var(--on-soft)', textTransform: 'uppercase', letterSpacing: 0.4, padding: '0 4px 8px' }}>
                      {t('reports.censusZipLabel', 'ZIP {z}').replace('{z}', selectedZipData.zip)}
                      {selectedZipData.isPrimary ? ` · ${t('reports.censusYours', 'your ZIP')}` : ''}
                    </div>
                    {renderCards(selectedZipData.cbp, selectedZipData.acs, { densityLabel: t('reports.censusDensity', 'Restaurants here') })}
                  </>
                )}

                <div style={{ fontSize: 11, color: 'var(--on-soft)', lineHeight: 1.5, padding: '4px 4px 0' }}>
                  {t('reports.censusSources', 'Sources: US Census Bureau · County Business Patterns {cbp} · ACS {acs}')
                    .replace('{cbp}', String(payload?.vintages?.cbp || ''))
                    .replace('{acs}', String(payload?.vintages?.acs || ''))}
                </div>
              </>
            );
          })()}
        </div>
      </div>
    </KShell>
  );
}

// ─── Rent & utilities ─────────────────────────────────────────────────────
// Tracks rent + NNN payments by ISO month with a year-over-year headline. NNN
// (triple-net) charges grow faster than the lease-capped base rent — keeping
// an eye on the running total catches reconciliation creep before the EOY
// landlord statement does.
// Annual CAM statement categories the OCR prompt returns. Stable order so the
// YoY comparison column renders predictably.
const CAM_CATEGORIES = [
  ['baseRent',    'reports.camBaseRent',    'Base rent'],
  ['propertyTax', 'reports.camPropertyTax', 'Property tax'],
  ['insurance',   'reports.camInsurance',   'Insurance'],
  ['cam',         'reports.camCam',         'CAM'],
  ['utilities',   'reports.camUtilities',   'Utilities'],
  ['trash',       'reports.camTrash',       'Trash'],
  ['management',  'reports.camManagement',  'Management'],
  ['hvac',        'reports.camHvac',        'HVAC'],
  ['repairs',     'reports.camRepairs',     'Repairs'],
];

function RentReport({ dark = false, persona = 'owner' }) {
  useT();
  const nav = React.useContext(KanjoNav);
  // Extra 60d for prior-year YoY context; card charges included — utility bills
  // (PSE etc.) are often paid on the card, and card charges are spend on the
  // day charged.
  const { loading, txs, currency, locale, error } = useTrailingTxs(365 + 60, { includeCardCharges: true });
  const [keywords, setKeywords] = React.useState(null);
  const [statements, setStatements] = React.useState([]);
  const [statementsNonce, setStatementsNonce] = React.useState(0);
  const reloadStatements = React.useCallback(() => setStatementsNonce((n) => n + 1), []);
  const [scan, setScan] = React.useState({ phase: 'idle' });
  const [cfgOpen, setCfgOpen] = React.useState(false);
  const camFileRef = React.useRef(null);
  const [expKw, setExpKw] = React.useState(null); // utility/maintenance keyword map (has defaults)
  const [tf, setTf] = React.useState('12mo');     // same timeframe filter as Payroll/Taxes
  const loadKeywords = () => {
    window.kanjo?.cashflow?.rent?.getKeywords?.().then(setKeywords).catch(() => setKeywords([]));
    window.kanjo?.cashflow?.expenseCategories?.getKeywords?.().then(setExpKw).catch(() => setExpKw(null));
  };
  React.useEffect(() => { loadKeywords(); }, []);
  React.useEffect(() => {
    (async () => {
      try {
        const claims = await window.kanjo?.auth?.currentClaims?.().catch(() => null);
        if (!claims) return;
        const rows = await window.kanjo.db.landlordStatements.where('userId').equals(claims.sub).toArray();
        rows.sort((a, b) => (b.statementYear || 0) - (a.statementYear || 0));
        setStatements(rows);
      } catch (e) { console.warn('[rent] load statements failed', e); }
    })();
  }, [statementsNonce]);

  const scanCam = async (blob) => {
    setScan({ phase: 'reading' });
    try {
      const mime = blob.type && /^(image\/(jpeg|png|webp)|application\/pdf)$/.test(blob.type)
        ? blob.type : 'image/jpeg';
      const { receiptId } = await window.kanjo.ocr.uploadReceiptImage(blob, mime, { kind: 'landlord' });
      const result = await window.kanjo.ocr.pollOcrResult(receiptId, { kind: 'landlord', timeoutMs: 60_000 });
      const claims = await window.kanjo.auth.currentClaims();
      const row = {
        statementId: receiptId,
        userId: claims.sub,
        statementYear: Number(result?.statementYear) || new Date().getFullYear() - 1,
        photo: blob,
        mimeType: mime,
        createdAt: Date.now(),
        fields: result || {},
      };
      await window.kanjo.db.landlordStatements.put(row);
      reloadStatements();
      setScan({ phase: 'done' });
    } catch (e) {
      console.warn('[rent] CAM OCR failed', e);
      setScan({ phase: 'error' });
    }
  };
  const onCamFile = (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!f) return;
    scanCam(f);
  };
  const deleteStatement = async (statementId) => {
    if (!window.confirm(t('reports.rentDeleteConfirm', 'Delete this statement?'))) return;
    try {
      await window.kanjo.db.landlordStatements.delete(statementId);
      reloadStatements();
    } catch (e) { console.warn('[rent] delete failed', e); }
  };

  // Rent keywords + utility-classified bills (PSE, NW Natural, Comcast, …) —
  // the same union the cash-flow Sankey's rent_utilities category shows.
  // `sum` stays on the FULL pull (365+60d) so YoY and the 12-month trend keep
  // their context regardless of the timeframe toggle.
  const sum = React.useMemo(() => {
    if (!keywords) return null;
    return window.kanjo.cashflow.rent.summarize(txs, keywords, expKw);
  }, [txs, keywords, expKw]);
  const noKeywords = keywords && keywords.length === 0;

  // Timeframe-scoped totals + activity (this quarter / YTD / last 12 months),
  // mirroring the Payroll report. The pull is 425 days for YoY context, so the
  // 12mo cutoff is applied explicitly rather than assumed.
  const scoped = React.useMemo(() => {
    if (!sum) return null;
    const cutoff = timeframeCutoffIso(tf);
    const rows = [];
    const totals = { rent: 0, utilities: 0, mro: 0, total: 0 };
    for (const m of sum.monthly) {
      for (const r of m.rows) {
        if ((r.date || '') < cutoff) continue;
        rows.push(r);
        totals.total += r.amountCents || 0;
        if (r._bucket === 'utility') totals.utilities += r.amountCents || 0;
        else if (r._bucket === 'mro') totals.mro += r.amountCents || 0;
        else totals.rent += r.amountCents || 0;
      }
    }
    rows.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
    return { totals, rows };
  }, [sum, tf]);
  const headlineLabel = timeframeLabel(tf, new Date(), locale);

  // Pair-wise YoY comparison: take the two newest distinct statement years
  // and show category-by-category deltas.
  const camYoy = React.useMemo(() => {
    if (statements.length < 1) return null;
    const cur = statements[0];
    const prior = statements.find((s) => s.statementYear < cur.statementYear) || null;
    return { cur, prior };
  }, [statements]);

  // 12-month bar series for the trend card.
  const monthly12 = React.useMemo(() => {
    if (!sum) return null;
    const today = new Date();
    const months = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const found = sum.monthly.find((m) => m.ym === ym);
      // Split per month so the trend can stack rent vs utilities vs MRO — a
      // utility bill's seasonal swing or a one-off repair is invisible inside
      // a bar dominated by flat rent.
      months.push({ ym, label: d.toLocaleDateString(locale || 'en-US', { month: 'short', timeZone: 'UTC' }),
        total: found ? found.total : 0,
        utilTotal: found ? found.utilTotal : 0,
        rentTotal: found ? found.rentTotal : 0,
        mroTotal: found ? found.mroTotal : 0 });
    }
    return months;
  }, [sum, locale]);

  return (
    <KShell dark={dark} tab="reports" persona={persona}>
      <div style={{ height: '100%', overflowY: 'auto', paddingBottom: 24 }}>
        <KHeader back title={t('reports.rent', 'Rent, utilities & maintenance')} subtitle={t('reports.rentSub', 'Detected from bank activity')}
          right={<GearButton label={t('account.landlordKeywords', 'Landlord keywords')} onClick={() => setCfgOpen(true)} />} />
        <Sheet open={cfgOpen} onClose={() => { setCfgOpen(false); loadKeywords(); }} title={t('reports.rent', 'Rent, utilities & maintenance')}>
          <RentUtilitySettings />
        </Sheet>
        <div style={{ padding: '0 20px' }}>
          {error && <ErrorBanner>{error}</ErrorBanner>}
          {(loading || !sum) && !error && <SkeletonCard height={120}/>}
          {sum && !loading && noKeywords && (
            <div className="card" style={{ padding: 20 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>
                {t('reports.rentSetupTitle', 'Tell Kanjo what your rent payments look like')}
              </div>
              <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.55, marginTop: 8 }}>
                {t('reports.rentSetupBody', "Tap the gear above and add the descriptors that appear on your rent and NNN transactions — typically the landlord or property-management company name. Any matching outflow lands here as rent.")}
              </div>
              <button onClick={() => setCfgOpen(true)} className="btn primary" style={{ marginTop: 14 }}>
                {t('reports.rentSetupOpen', 'Add landlord keywords')}
              </button>
            </div>
          )}
          {/* Utilities have default keywords, so bills can surface before any
              landlord keyword is saved — show them under the setup nudge. */}
          {sum && scoped && !loading && (!noKeywords || sum.total > 0) && (() => {
            const yoyDelta = sum.ytdPrior > 0 ? ((sum.ytdThis - sum.ytdPrior) / sum.ytdPrior) * 100 : null;
            const yoyUp = yoyDelta != null && yoyDelta > 0;
            const maxMonth = Math.max(1, ...(monthly12 || []).map((m) => m.total));
            return (
              <>
                <TimeframeTabs value={tf} onChange={setTf} />

                {/* Timeframe-scoped headline (this quarter / YTD / last 12
                    months, like Payroll) — combined rent + utilities + MRO,
                    with the split spelled out when more than one is present. */}
                <div className="card" style={{ padding: 22, marginBottom: 14 }}>
                  <div className="label">{t('reports.rentScoped', 'Rent, utilities & maintenance · {tf}').replace('{tf}', headlineLabel)}</div>
                  <div style={{ marginTop: 6 }}>
                    <KMoney cents={scoped.totals.total} currency={currency} locale={locale} size="xl" tone="danger"/>
                  </div>
                  {(() => {
                    const split = [
                      [t('reports.rentSplitRent', 'Rent'), scoped.totals.rent],
                      [t('reports.rentSplitUtilities', 'Utilities'), scoped.totals.utilities],
                      [t('reports.rentSplitMro', 'Maintenance'), scoped.totals.mro],
                    ].filter(([, c]) => c > 0);
                    if (split.length < 2) return null;
                    return (
                      <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--rule)',
                        display: 'grid', gridTemplateColumns: `repeat(${split.length}, 1fr)`, gap: 10 }}>
                        {split.map(([label, cents]) => (
                          <div key={label}>
                            <div className="label">{label}</div>
                            <div style={{ marginTop: 4 }}>
                              <KMoney cents={cents} currency={currency} locale={locale} style={{ fontSize: 13, fontWeight: 600 }}/>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>

                {/* YoY comparison — the headline that catches NNN creep. */}
                <div className="card" style={{ padding: 18, marginBottom: 14 }}>
                  <div className="label" style={{ marginBottom: 10 }}>
                    {t('reports.rentYoy', 'Year-over-year · YTD {year}').replace('{year}', String(sum.thisYear))}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    <div>
                      <div className="label" style={{ fontSize: 11 }}>{String(sum.thisYear)}</div>
                      <div style={{ marginTop: 4 }}>
                        <KMoney cents={sum.ytdThis} currency={currency} locale={locale}
                          style={{ fontSize: 18, fontWeight: 700 }} tone="danger"/>
                      </div>
                    </div>
                    <div>
                      <div className="label" style={{ fontSize: 11 }}>{String(sum.priorYear)} · {t('reports.rentSamePeriod', 'same period')}</div>
                      <div style={{ marginTop: 4 }}>
                        <KMoney cents={sum.ytdPrior} currency={currency} locale={locale}
                          style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink-2)' }}/>
                      </div>
                    </div>
                  </div>
                  {yoyDelta != null && (
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--rule)',
                      fontSize: 13, color: 'var(--ink-2)' }}>
                      <span style={{ fontWeight: 700, color: yoyUp ? 'var(--danger,#d2563f)' : 'var(--positive,#2e9e5b)' }}>
                        {yoyUp ? '▲' : '▼'} {Math.abs(yoyDelta).toFixed(yoyDelta < 10 ? 1 : 0)}%
                      </span>{' '}
                      {t('reports.rentVsPrior', 'vs same period last year')}
                    </div>
                  )}
                  {/* Per-bucket YTD lines — utilities and MRO, so a creeping
                      combined total shows WHICH side is moving. */}
                  {(() => {
                    const lines = [
                      [t('reports.rentSplitUtilities', 'Utilities'), sum.ytdThisBy.utility, sum.ytdPriorBy.utility],
                      [t('reports.rentSplitMro', 'Maintenance'), sum.ytdThisBy.mro, sum.ytdPriorBy.mro],
                    ].filter(([, a, b]) => a > 0 || b > 0);
                    if (lines.length === 0) return null;
                    return (
                      <div style={{ marginTop: 10, paddingTop: 4 }}>
                        {lines.map(([label, a, b]) => {
                          const d = b > 0 ? ((a - b) / b) * 100 : null;
                          const up = d != null && d > 0;
                          return (
                            <div key={label} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto',
                              gap: 12, padding: '7px 0', borderTop: '1px solid var(--hairline,#eee)',
                              alignItems: 'center', fontSize: 12.5 }}>
                              <span style={{ color: 'var(--ink-2)' }}>{label}</span>
                              <span style={{ textAlign: 'right' }}>
                                <KMoney cents={a} currency={currency} locale={locale} style={{ fontSize: 12.5, fontWeight: 600 }}/>
                              </span>
                              <span style={{ textAlign: 'right' }}>
                                {b > 0
                                  ? <KMoney cents={b} currency={currency} locale={locale} style={{ fontSize: 12.5, color: 'var(--ink-3)' }}/>
                                  : <span style={{ color: 'var(--ink-3)' }}>—</span>}
                              </span>
                              <span className="mono" style={{ textAlign: 'right', fontSize: 12, minWidth: 52, fontWeight: 700,
                                color: d == null ? 'var(--ink-3)' : up ? 'var(--danger,#d2563f)' : 'var(--positive,#2e9e5b)' }}>
                                {d == null ? '—' : `${up ? '+' : ''}${d.toFixed(Math.abs(d) < 10 ? 1 : 0)}%`}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>

                {/* CAM reconciliation — context for what the NNN charges
                    actually paid for. The amounts here don't change the rent
                    totals above; they explain them so YoY drift becomes
                    explicit (which line item ballooned). */}
                <div className="card" style={{ padding: 16, marginBottom: 14 }}>
                  <input ref={camFileRef} type="file" accept="image/*,application/pdf" onChange={onCamFile}
                    style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
                      overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}/>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10 }}>
                    <div>
                      <div className="label">{t('reports.camTitle', 'CAM reconciliation')}</div>
                      <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
                        {t('reports.camHint', 'Scan your annual landlord statement to see year-over-year line items')}
                      </div>
                    </div>
                    <button onClick={() => camFileRef.current && camFileRef.current.click()}
                      disabled={scan.phase === 'reading'}
                      style={{ flexShrink: 0, padding: '9px 14px', borderRadius: 10,
                        border: '1px solid var(--rule)', background: 'var(--surface)', color: 'var(--ink)',
                        fontSize: 13, fontWeight: 600,
                        cursor: scan.phase === 'reading' ? 'default' : 'pointer',
                        opacity: scan.phase === 'reading' ? 0.6 : 1 }}>
                      {t('reports.camScan', 'Scan statement')}
                    </button>
                  </div>
                  {scan.phase === 'reading' && (
                    <div style={{ fontSize: 12, color: 'var(--brand,#3a5fbf)', marginBottom: 10 }}>
                      {t('reports.camReading', 'Reading the statement…')}
                    </div>
                  )}
                  {scan.phase === 'error' && (
                    <div style={{ fontSize: 12, color: 'var(--warning,#9a6700)', marginBottom: 10 }}>
                      {t('reports.camFailed', "Couldn't read the statement — try a clearer scan.")}
                    </div>
                  )}

                  {camYoy && camYoy.cur && (
                    <div style={{ borderTop: '1px solid var(--rule)', paddingTop: 12 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: 12,
                        fontSize: 10.5, color: 'var(--ink-3)', fontWeight: 600, padding: '6px 0' }}>
                        <span>{t('reports.camCategory', 'Category')}</span>
                        <span style={{ textAlign: 'right' }}>{camYoy.cur.statementYear}</span>
                        <span style={{ textAlign: 'right' }}>{camYoy.prior ? camYoy.prior.statementYear : '—'}</span>
                        <span style={{ textAlign: 'right', minWidth: 60 }}>Δ</span>
                      </div>
                      {CAM_CATEGORIES.map(([k, key, fb]) => {
                        const a = Number(camYoy.cur.fields?.[k]) || 0;
                        const b = camYoy.prior ? (Number(camYoy.prior.fields?.[k]) || 0) : null;
                        if (a === 0 && (b == null || b === 0)) return null;
                        const delta = b != null && b !== 0 ? ((a - b) / b) * 100 : null;
                        const up = delta != null && delta > 0;
                        return (
                          <div key={k} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto',
                            gap: 12, padding: '8px 0', borderTop: '1px solid var(--hairline,#eee)',
                            alignItems: 'center', fontSize: 13 }}>
                            <span style={{ color: 'var(--ink)' }}>{t(key, fb)}</span>
                            <span style={{ textAlign: 'right' }}>
                              <KMoney cents={Math.round(a * 100)} currency={currency} locale={locale}
                                style={{ fontSize: 13, fontWeight: 600 }}/>
                            </span>
                            <span style={{ textAlign: 'right' }}>
                              {b != null
                                ? <KMoney cents={Math.round(b * 100)} currency={currency} locale={locale}
                                    style={{ fontSize: 13, color: 'var(--ink-3)' }}/>
                                : <span style={{ color: 'var(--ink-3)' }}>—</span>}
                            </span>
                            <span className="mono" style={{ textAlign: 'right', fontSize: 12.5, minWidth: 60,
                              fontWeight: 700,
                              color: delta == null ? 'var(--ink-3)' : up ? 'var(--danger,#d2563f)' : 'var(--positive,#2e9e5b)' }}>
                              {delta == null ? '—' : `${up ? '+' : ''}${delta.toFixed(delta < 10 ? 1 : 0)}%`}
                            </span>
                          </div>
                        );
                      })}
                      {/* Reconciliation: positive = tenant owes; negative = credit. */}
                      {(Number(camYoy.cur.fields?.reconciliation) || 0) !== 0 && (
                        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--rule)',
                          fontSize: 13, color: 'var(--ink-2)', display: 'flex',
                          alignItems: 'center', justifyContent: 'space-between' }}>
                          <span>{t('reports.camReconciliation', 'Reconciliation')}</span>
                          <KMoney cents={Math.round((camYoy.cur.fields?.reconciliation || 0) * 100)}
                            currency={currency} locale={locale}
                            tone={(camYoy.cur.fields?.reconciliation || 0) > 0 ? 'danger' : 'positive'}
                            style={{ fontSize: 14, fontWeight: 700 }}/>
                        </div>
                      )}
                      <button onClick={() => deleteStatement(camYoy.cur.statementId)}
                        style={{ marginTop: 10, background: 'none', border: 'none', color: 'var(--ink-3)',
                          fontSize: 11.5, cursor: 'pointer', padding: 0 }}>
                        {t('reports.camRemove', 'Remove this statement')}
                      </button>
                    </div>
                  )}
                  {statements.length === 0 && scan.phase !== 'reading' && (
                    <div style={{ fontSize: 12.5, color: 'var(--ink-3)', borderTop: '1px solid var(--rule)', paddingTop: 12 }}>
                      {t('reports.camEmpty', 'No statements yet. Scan one to start tracking what your NNN charges actually pay for.')}
                    </div>
                  )}
                </div>

                {/* Monthly trend */}
                {monthly12 && (() => {
                  const anyUtil = monthly12.some((m) => m.utilTotal > 0);
                  const anyMro = monthly12.some((m) => m.mroTotal > 0);
                  const money = (c) => (c / 100).toLocaleString(locale || 'en-US', { style: 'currency', currency });
                  return (
                    <div className="card" style={{ padding: 16, marginBottom: 14 }}>
                      <div className="label" style={{ marginBottom: 10 }}>{t('reports.rentMonthly', 'Monthly trend · last 12 months')}</div>
                      {/* Stacked: rent (red, bottom) + utilities (amber) + MRO
                          (brand, top) so the utility/repair layers read even
                          against a flat, dominant rent. */}
                      <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', height: 96 }}>
                        {monthly12.map((m) => {
                          const isCurrent = m.ym === sum.thisYm;
                          const h = (c) => c > 0 ? Math.max(3, Math.round((c / maxMonth) * 88)) : 0;
                          // Top-to-bottom stack order; radius on the outer edges only.
                          const segs = [
                            { h: h(m.mroTotal), bg: 'var(--brand,#5b8def)' },
                            { h: h(m.utilTotal), bg: 'var(--warning,#b06000)' },
                            { h: h(m.rentTotal), bg: 'var(--danger,#d2563f)' },
                          ].filter((s) => s.h > 0);
                          const parts = [];
                          if (m.mroTotal > 0) parts.push(`${t('reports.rentSplitMro', 'Maintenance')} ${money(m.mroTotal)}`);
                          if (m.utilTotal > 0) parts.push(`${t('reports.rentSplitUtilities', 'Utilities')} ${money(m.utilTotal)}`);
                          if (m.rentTotal > 0 && parts.length > 0) parts.unshift(`${t('reports.rentSplitRent', 'Rent')} ${money(m.rentTotal)}`);
                          const title = m.total > 0
                            ? `${m.label} — ${money(m.total)}${parts.length > 1 ? ` (${parts.join(' · ')})` : ''}`
                            : `${m.label} — ${money(0)}`;
                          return (
                            <div key={m.ym} title={title}
                              style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
                                height: '100%', opacity: isCurrent ? 0.55 : 1 }}>
                              {segs.length === 0 && <div style={{ height: 2, borderRadius: 3, background: 'var(--surface-3)' }}/>}
                              {segs.map((s, i) => (
                                <div key={i} style={{ height: s.h, background: s.bg,
                                  borderRadius: segs.length === 1 ? 3
                                    : i === 0 ? '3px 3px 0 0'
                                    : i === segs.length - 1 ? '0 0 3px 3px' : 0 }}/>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                      <div style={{ display: 'flex', gap: 4, marginTop: 6, fontSize: 11, color: 'var(--ink-3)' }}>
                        {monthly12.map((m) => (<div key={m.ym} style={{ flex: 1, textAlign: 'center' }}>{m.label}</div>))}
                      </div>
                      {(anyUtil || anyMro) && (
                        <div style={{ display: 'flex', gap: 14, marginTop: 8, fontSize: 11.5, color: 'var(--ink-2)' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                            <span style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--danger,#d2563f)' }}/>{t('reports.rentSplitRent', 'Rent')}
                          </span>
                          {anyUtil && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                              <span style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--warning,#b06000)' }}/>{t('reports.rentSplitUtilities', 'Utilities')}
                            </span>
                          )}
                          {anyMro && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                              <span style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--brand,#5b8def)' }}/>{t('reports.rentSplitMro', 'Maintenance')}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Timeframe-scoped activity */}
                <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 14 }}>
                  <div className="label" style={{ padding: '14px 16px 6px' }}>
                    {t('reports.rentActivityScoped', 'Activity · {tf}').replace('{tf}', headlineLabel)}
                  </div>
                  {scoped.rows.length === 0 ? (
                    <div style={{ padding: '12px 16px', fontSize: 12.5, color: 'var(--ink-3)' }}>
                      {t('reports.rentNoActivityTf', 'No rent, utility, or maintenance activity in this timeframe.')}
                    </div>
                  ) : scoped.rows.map((tx) => (
                    <div key={tx.plaidTxId}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '12px 16px', borderTop: '1px solid var(--rule)' }}>
                      <span style={{ minWidth: 0, paddingRight: 12 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink)' }}>
                          {txLabel(tx)}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
                          {fmtDateMdy(tx.date, locale)}
                          {tx._bucket === 'utility' && <> · {t('reports.rentRowUtility', 'utility')}</>}
                          {tx._bucket === 'mro' && <> · {t('reports.rentRowMro', 'maintenance')}</>}
                        </div>
                      </span>
                      <KMoney cents={tx.amountCents} currency={currency} locale={locale} tone="danger"
                        style={{ fontSize: 14, fontWeight: 700 }}/>
                    </div>
                  ))}
                </div>

                {/* NNN context */}
                <div style={{ fontSize: 11.5, color: 'var(--on-dim)', marginTop: 12, lineHeight: 1.5 }}>
                  {t('reports.rentFootnote', "Rent + NNN (property tax, insurance, CAM, utilities) typically grows faster than your lease-capped base rent. The year-over-year line above is the one to watch — when it drifts up faster than your inflation cap, the CAM reconciliation is where it's happening.")}
                </div>
              </>
            );
          })()}
        </div>
      </div>
    </KShell>
  );
}

// ─── Supplies ─────────────────────────────────────────────────────────────
// Restaurant non-bank-detected supply purchases (Amazon, Costco,
// Restaurant Depot, …). Imported via CSV that either comes from the
// canonical Amazon-shaped format (samples/amazon.csv) OR from the
// "Copy extraction prompt" → Claude Web flow that normalizes any
// source page/PDF into the same CSV.
function SuppliesReport({ dark = false, persona = 'owner' }) {
  useT();
  // Supplies come from the CREDIT-CARD feed: charges the operator categorized
  // "Supplies" on the Spending tab (per-merchant rules apply automatically).
  // The Amazon order-history CSV upload was removed 2026-07-11 (owner: Plaid
  // is the source); previously imported orders remain in db.supplies but are
  // no longer read here or by the Sankey.
  const [cardCharges, setCardCharges] = React.useState(null); // null = loading
  const [profile, setProfile] = React.useState({ currency: 'USD', locale: 'en-US' });

  const load = React.useCallback(async () => {
    const claims = await window.kanjo?.auth?.currentClaims?.().catch(() => null);
    if (!claims) { setCardCharges([]); return; }
    const db = window.kanjo.db;
    const prof = await db.userProfile.get(claims.sub).catch(() => null);
    setProfile({ currency: prof?.currency || 'USD', locale: prof?.locale || 'en-US' });
    try {
      const acct = window.kanjo.cashflow.accounts;
      const sc = window.kanjo.cashflow.spend;
      const creditSet = await acct.creditAccountIds(claims.sub);
      // 365 days covers YTD and the 12-month trend. Card refunds (negative)
      // are kept so the totals net out, same as the Spending tab; bank rows
      // count outflows only (a deposit is never supplies) and reach here via
      // the Today tile's categorize picker.
      const cutoff = new Date(Date.now() - 365 * 86400000).toISOString().slice(0, 10);
      const txs = await db.plaidTransactions
        .where('[userId+date]').between([claims.sub, cutoff], [claims.sub, localToday()], true, true)
        .toArray();
      const ctx = await sc.loadExplicitContext(claims.sub);
      const charges = txs.filter((t) => !t.pending
        && (creditSet.has(t.plaidAccountId) || (t.amountCents || 0) > 0)
        && sc.explicitCategory(t, ctx) === 'supplies')
        .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
      setCardCharges(charges);
    } catch (e) { console.warn('[supplies] card charges load failed', e); setCardCharges([]); }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const today = new Date();
  const sums = React.useMemo(() => {
    if (!cardCharges) return null;
    const y = today.getFullYear(), m = today.getMonth();
    const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const ytdFrom = `${y}-01-01`;
    const qFrom = iso(new Date(y, Math.floor(m / 3) * 3, 1));
    const mFrom = iso(new Date(y, m, 1));
    let ytd = 0, quarter = 0, month = 0;
    const byYm = new Map();
    for (const c of cardCharges) {
      const cents = c.amountCents || 0;
      if (c.date >= ytdFrom) ytd += cents;
      if (c.date >= qFrom) quarter += cents;
      if (c.date >= mFrom) month += cents;
      const ym = c.date.slice(0, 7);
      byYm.set(ym, (byYm.get(ym) || 0) + cents);
    }
    const monthly = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(y, m - i, 1);
      const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      monthly.push({ ym, total: byYm.get(ym) || 0 });
    }
    return { totals: { ytd, quarter, month }, monthly };
  }, [cardCharges]);

  const M = (c) => <KMoney cents={c} currency={profile.currency} locale={profile.locale} style={{ fontSize: 13, fontWeight: 600 }}/>;
  const xl = (c, tone) => <KMoney cents={c} currency={profile.currency} locale={profile.locale} size="xl" tone={tone}/>;

  const thisYm = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  const maxMonth = sums ? Math.max(1, ...sums.monthly.map((m) => m.total)) : 1;
  const labelOf = (ym) => {
    const [y, mo] = ym.split('-').map(Number);
    return new Date(y, (mo || 1) - 1, 1).toLocaleDateString(profile.locale, { month: 'short', timeZone: 'UTC' });
  };

  return (
    <KShell dark={dark} tab="reports" persona={persona}>
      <div style={{ height: '100%', overflowY: 'auto', paddingBottom: 24 }}>
        <KHeader back title={t('reports.supplies', 'Supplies')} subtitle={t('reports.suppliesSubCard', 'Card charges categorized Supplies on the Spending tab')} />
        <div style={{ padding: '0 20px' }}>

          {!cardCharges && <SkeletonCard height={120}/>}
          {cardCharges && cardCharges.length === 0 && (
            <>
              <Empty title={t('reports.suppliesEmpty2', 'No supplies charges yet')}
                body={t('reports.suppliesEmptyBody2', 'Categorize a card charge as Supplies on the Spending tab and it shows up here — the per-merchant rule then catches every future charge from that store automatically.')} />
              <KLink to="/spending" asButton>
                <button className="btn primary" style={{ width: '100%', marginTop: 4 }}>
                  {t('reports.suppliesGoSpending', 'Open the Spending tab')}
                </button>
              </KLink>
            </>
          )}

          {sums && cardCharges && cardCharges.length > 0 && (
            <>
              {/* Three-up headline: YTD · This quarter · This month */}
              <div className="card" style={{ padding: 22, marginBottom: 14 }}>
                <div className="label">{t('reports.suppliesYtd', 'Year to date')} · {today.getFullYear()}</div>
                <div style={{ marginTop: 6 }}>{xl(sums.totals.ytd, 'danger')}</div>
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--rule)',
                  display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <div className="label">{t('reports.suppliesQuarter', 'This quarter')}</div>
                    <div style={{ marginTop: 4 }}>{M(sums.totals.quarter)}</div>
                  </div>
                  <div>
                    <div className="label">{t('reports.suppliesMonth', 'This month')}</div>
                    <div style={{ marginTop: 4 }}>{M(sums.totals.month)}</div>
                  </div>
                </div>
              </div>

              {/* 12-month chart */}
              <div className="card" style={{ padding: 16, marginBottom: 14 }}>
                <div className="label" style={{ marginBottom: 10 }}>{t('reports.suppliesMonthly', 'Monthly trend · last 12 months')}</div>
                <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', height: 96 }}>
                  {sums.monthly.map((m) => {
                    const h = m.total > 0 ? Math.max(3, Math.round((m.total / maxMonth) * 88)) : 2;
                    const isCurrent = m.ym === thisYm;
                    return (
                      <div key={m.ym}
                        title={`${labelOf(m.ym)} — ${(m.total / 100).toLocaleString(profile.locale, { style: 'currency', currency: profile.currency })}`}
                        style={{ flex: 1, height: h, borderRadius: 3,
                          background: m.total > 0 ? 'var(--danger,#d2563f)' : 'var(--surface-3)',
                          opacity: isCurrent ? 0.55 : 1 }}/>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', gap: 4, marginTop: 6, fontSize: 11, color: 'var(--ink-3)' }}>
                  {sums.monthly.map((m) => (
                    <div key={m.ym} style={{ flex: 1, textAlign: 'center' }}>{labelOf(m.ym)}</div>
                  ))}
                </div>
              </div>

              {/* Charges list — refunds show green and net out of the totals. */}
              <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 14 }}>
                <div className="label" style={{ padding: '14px 16px 6px' }}>
                  {t('reports.suppliesCharges', 'Charges · {n}').replace('{n}', cardCharges.length)}
                </div>
                {cardCharges.slice(0, 30).map((c) => (
                  <div key={c.plaidTxId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    gap: 10, padding: '11px 16px', borderTop: '1px solid var(--rule)' }}>
                    <span style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {txLabel(c)}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>{fmtDateMdy(c.date, profile.locale)}</div>
                    </span>
                    <KMoney cents={c.amountCents} currency={profile.currency} locale={profile.locale}
                      tone={(c.amountCents || 0) < 0 ? 'positive' : 'danger'}
                      style={{ fontSize: 14, fontWeight: 700, flexShrink: 0 }}/>
                  </div>
                ))}
                {cardCharges.length > 30 && (
                  <div style={{ fontSize: 11.5, color: 'var(--ink-3)', padding: '10px 16px' }}>
                    +{cardCharges.length - 30} {t('reports.suppliesMore', 'more on the Spending tab')}
                  </div>
                )}
              </div>

              <div style={{ fontSize: 11.5, color: 'var(--on-dim)', marginTop: 12, lineHeight: 1.5 }}>
                {t('reports.suppliesFootnote2', 'Charges come from your connected credit cards, categorized as Supplies on the Spending tab. A per-merchant rule ("every charge from this merchant") keeps future charges flowing here automatically.')}
              </div>
            </>
          )}
        </div>
      </div>
    </KShell>
  );
}

// ─── Insurance ──────────────────────────────────────────────────────────────
// Premiums detected by keyword (default "Farmers"), totalled YTD / last 12
// months, with a dedicated panel for the most recent rate step-up (the annual
// increase: one installment jumps up from the prior one).
function InsuranceReport({ dark = false, persona = 'owner' }) {
  useT();
  // ~16 mo: full year + the prior installment to compare. Card charges included —
  // a premium autopaid on the credit card is spend on the day charged (owner card
  // model), same basis as the Rent & utilities report; the bill-payment legs are
  // dropped so it's never double-counted. (Depository direct-debit premiums are
  // covered either way — card charges are a superset.)
  const { loading, txs, currency, locale, error } = useTrailingTxs(365 + 120, { includeCardCharges: true });
  const [keywords, setKeywords] = React.useState(null);
  const [kwDraft, setKwDraft] = React.useState('');
  const [range, setRange] = React.useState('ytd'); // 'ytd' | '12mo'

  React.useEffect(() => {
    window.kanjo?.cashflow?.insurance?.getKeywords?.()
      .then((k) => { setKeywords(k || []); setKwDraft((k || []).join(', ')); })
      .catch(() => setKeywords([]));
  }, []);

  const sum = React.useMemo(() => {
    if (!keywords) return null;
    return window.kanjo.cashflow.insurance.summarize(txs, keywords);
  }, [txs, keywords]);

  const saveKeywords = async () => {
    const list = kwDraft.split(',').map((s) => s.trim()).filter(Boolean);
    try {
      const saved = await window.kanjo.cashflow.insurance.setKeywords(list);
      setKeywords(saved); setKwDraft(saved.join(', '));
    } catch (e) { console.warn('[insurance] save keywords failed', e); }
  };

  const fmt = (c) => new Intl.NumberFormat(locale || 'en-US', { style: 'currency', currency: currency || 'USD' }).format((c || 0) / 100);
  const fmtDate = (iso) => new Date(iso + 'T00:00:00Z').toLocaleDateString(locale || 'en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });

  const windowTotal = sum ? (range === 'ytd' ? sum.ytdCents : sum.ttmCents) : 0;
  const windowLabel = range === 'ytd' ? t('reports.insYtdLong', 'year to date') : t('reports.ins12Long', 'last 12 months');
  const inc = sum && sum.latestIncrease;

  return (
    <KShell dark={dark} tab="reports" persona={persona}>
      <div style={{ height: '100%', overflowY: 'auto', paddingBottom: 24 }}>
        <KHeader back title={t('reports.insurance', 'Insurance')} subtitle={t('reports.insuranceSub', 'Premiums detected from bank activity')} />
        <div style={{ padding: '0 20px' }}>
          {error && <ErrorBanner>{error}</ErrorBanner>}
          {(loading || !sum) && !error && <SkeletonCard height={140}/>}
          {sum && !loading && (
            <>
              {/* YTD / Last 12 months */}
              <div role="tablist" aria-label="Window" style={{ display: 'flex', padding: 3, gap: 3, background: 'var(--surface-3,#f0f0f3)', borderRadius: 8, marginBottom: 14 }}>
                {[['ytd', t('reports.insYtd', 'YTD')], ['12mo', t('reports.ins12', 'Last 12 months')]].map(([id, label]) => {
                  const on = range === id;
                  return (
                    <button key={id} role="tab" aria-selected={on} onClick={() => setRange(id)}
                      style={{ flex: 1, padding: '7px 10px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12.5, fontWeight: 600,
                        background: on ? 'var(--surface,#fff)' : 'transparent', color: on ? 'var(--ink)' : 'var(--ink-3)', boxShadow: on ? '0 1px 2px rgba(0,0,0,0.06)' : 'none' }}>{label}</button>
                  );
                })}
              </div>

              {/* headline total */}
              <div className="card" style={{ padding: 20, marginBottom: 14 }}>
                <div className="label">{t('reports.insurance', 'Insurance')} · {windowLabel}</div>
                <div className="figure" style={{ fontSize: 34, marginTop: 6 }}>{fmt(windowTotal)}</div>
                <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 4 }}>
                  {sum.payments.length} {sum.payments.length === 1 ? t('reports.insPayment', 'payment') : t('reports.insPayments', 'payments')} · {t('reports.insAllTime', 'all-time')} {fmt(sum.totalCents)}
                </div>
              </div>

              {/* rate-increase panel */}
              <div className="card" style={{ padding: 18, marginBottom: 14, borderLeft: inc ? '3px solid var(--danger)' : undefined }}>
                <div className="label">{t('reports.insIncrease', 'Rate increase')}</div>
                {inc ? (
                  <>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 8 }}>
                      <span className="figure" style={{ fontSize: 30, color: 'var(--danger)' }}>▲ {inc.pctIncrease.toFixed(1)}%</span>
                      <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>{t('reports.insOn', 'on')} {fmtDate(inc.date)}</span>
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 6 }}>
                      {fmt(inc.fromCents)} → <b style={{ color: 'var(--ink)' }}>{fmt(inc.toCents)}</b> {t('reports.insPerPayment', 'per payment')}
                      <span style={{ color: 'var(--ink-3)' }}> · +{fmt(inc.toCents - inc.fromCents)}</span>
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 8 }}>
                    {t('reports.insNoIncrease', 'No rate increase detected yet — premiums have been steady across payments.')}
                  </div>
                )}
              </div>

              {/* payments */}
              <div className="label" style={{ margin: '4px 4px 8px' }}>{t('reports.insPaymentsHeader', 'Payments')}</div>
              <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 14 }}>
                {sum.payments.length === 0 ? (
                  <div style={{ padding: 16, fontSize: 13, color: 'var(--ink-3)' }}>{t('reports.insNone', 'No matching insurance payments found — adjust the keywords below.')}</div>
                ) : (
                  [...sum.payments].reverse().map((p, i, a) => (
                    <div key={p.date + ':' + i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 16px', borderBottom: i < a.length - 1 ? '1px solid var(--rule)' : 'none' }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600 }}>{fmtDate(p.date)}</div>
                        <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.merchant}</div>
                      </div>
                      <div className="mono" style={{ fontSize: 14, fontWeight: 600, flexShrink: 0 }}>{fmt(p.amountCents)}</div>
                    </div>
                  ))
                )}
              </div>

              {/* detection keywords (setup) */}
              <div className="card" style={{ padding: 16, marginBottom: 14 }}>
                <div className="label">{t('reports.insDetection', 'Detection keywords')}</div>
                <div style={{ fontSize: 12.5, color: 'var(--ink-3)', margin: '6px 0 10px', lineHeight: 1.45 }}>
                  {t('reports.insDetectionBody', 'Comma-separated. Any outflow whose merchant or description contains one of these is counted as insurance.')}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input value={kwDraft} onChange={(e) => setKwDraft(e.target.value)} placeholder="Farmers, State Farm" aria-label="Insurance keywords"
                    style={{ flex: 1, padding: '9px 11px', borderRadius: 9, border: '1px solid var(--rule)', background: 'var(--surface)', color: 'var(--ink)', fontSize: 13.5 }} />
                  <button onClick={saveKeywords} className="btn" style={{ background: 'var(--brand-fill)', color: 'var(--on-brand-fill)', padding: '9px 16px' }}>{t('common.save', 'Save')}</button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </KShell>
  );
}

// ─── Capital expenses ───────────────────────────────────────────────────────
// One-off capital purchases (equipment, build-out) the operator tagged 'capex'
// on the Spending tab — kept out of the operating trend because they're lumpy.
// Shows YTD vs last year and a funding→merchant flow (card vs check → where it
// went). Spans card + checking via the shared spend-rule store (capex.js).
const CAPEX_SERIES = ['#7C3AED', '#2563EB', '#0D9488', '#DB2777', '#D97706', '#059669', '#64748B'];

function CapexFlow({ byMethod, byMerchant, total, currency, locale }) {
  const money = (c) => (c / 100).toLocaleString(locale || 'en-US', { style: 'currency', currency, maximumFractionDigits: 0 });
  const sources = [
    { id: 'card', label: t('reports.capexCard', 'Credit card'), cents: byMethod.card, color: '#7C3AED' },
    { id: 'bank', label: t('reports.capexCheck', 'Check / bank'), cents: byMethod.bank, color: '#0D9488' },
  ].filter((s) => s.cents > 0);
  // Cap the merchant column so the chart stays legible; fold the tail into "Other".
  const TOP = 6;
  let targets = byMerchant.map((m, i) => ({ id: 'm' + i, label: m.merchant, cents: m.cents, color: CAPEX_SERIES[i % CAPEX_SERIES.length] }));
  if (targets.length > TOP) {
    const head = targets.slice(0, TOP);
    const tail = targets.slice(TOP).reduce((s, m) => s + m.cents, 0);
    targets = [...head, { id: 'other', label: t('reports.capexOther', 'Other'), cents: tail, color: '#94A3B8' }];
  }
  const denom = Math.max(total, 1);
  const VBW = 440, TOP_Y = 10, DH = Math.max(120, targets.length * 34), GAP = 8, BARW = 9;
  const LBAR_L = 120, LBAR_R = LBAR_L + BARW;
  const CBAR_L = 214, CBAR_R = CBAR_L + BARW;
  const RBAR_L = 300, RBAR_R = RBAR_L + BARW;
  const stack = (items) => {
    const usable = DH - GAP * Math.max(0, items.length - 1);
    let y = TOP_Y;
    return items.map((it) => { const h = Math.max(5, (it.cents / denom) * usable); const s = { ...it, y, h }; y += h + GAP; return s; });
  };
  const LABELGAP = 20;
  const declutter = (segs) => { let prev = -1e9; return segs.map((s) => { let ly = s.y + s.h / 2; if (ly < prev + LABELGAP) ly = prev + LABELGAP; prev = ly; return { ...s, ly }; }); };
  const S = declutter(stack(sources)), Tg = declutter(stack(targets));
  const lastBottom = (a) => (a.length ? a[a.length - 1].y + a[a.length - 1].h : 0);
  const lastLabel = (a) => (a.length ? a[a.length - 1].ly + 12 : 0);
  const H = Math.max(lastBottom(S), lastBottom(Tg), lastLabel(S), lastLabel(Tg)) + 8;
  const ribbon = (xa, ya, ha, xb, yb, hb) => { const xc = (xa + xb) / 2; return `M${xa},${ya} C${xc},${ya} ${xc},${yb} ${xb},${yb} L${xb},${yb + hb} C${xc},${yb + hb} ${xc},${ya + ha} ${xa},${ya + ha} Z`; };
  return (
    <svg viewBox={`0 0 ${VBW} ${H}`} width="100%" style={{ display: 'block' }} role="img" aria-label={t('reports.capex', 'Capital expenses')}>
      {S.map((s) => <path key={'ls' + s.id} d={ribbon(LBAR_R, s.y, s.h, CBAR_L, s.y, s.h)} fill={s.color} opacity="0.26" />)}
      {Tg.map((tg) => <path key={'rt' + tg.id} d={ribbon(CBAR_R, tg.y, tg.h, RBAR_L, tg.y, tg.h)} fill={tg.color} opacity="0.26" />)}
      <rect x={CBAR_L} y={TOP_Y} width={BARW} height={H - 2 * TOP_Y} rx="2" fill="var(--ink-3)" opacity="0.5" />
      {S.map((s) => (
        <g key={'l' + s.id}>
          <rect x={LBAR_L} y={s.y} width={BARW} height={s.h} rx="2" fill={s.color} />
          <text x={LBAR_L - 7} y={s.ly - 1} textAnchor="end" style={{ fontSize: 11, fontWeight: 600, fill: 'var(--ink)' }}>{s.label}</text>
          <text x={LBAR_L - 7} y={s.ly + 11} textAnchor="end" style={{ fontSize: 9.5, fill: 'var(--ink-3)' }}>{money(s.cents)}</text>
        </g>
      ))}
      {Tg.map((tg) => (
        <g key={'r' + tg.id}>
          <rect x={RBAR_L} y={tg.y} width={BARW} height={tg.h} rx="2" fill={tg.color} />
          <text x={RBAR_R + 7} y={tg.ly - 1} style={{ fontSize: 11, fontWeight: 600, fill: 'var(--ink)' }}>{tg.label.length > 22 ? tg.label.slice(0, 21) + '…' : tg.label}</text>
          <text x={RBAR_R + 7} y={tg.ly + 11} style={{ fontSize: 9.5, fill: 'var(--ink-3)' }}>{money(tg.cents)} · {Math.round(tg.cents / denom * 100)}%</text>
        </g>
      ))}
    </svg>
  );
}

function CapexReport({ dark = false, persona = 'owner' }) {
  useT();
  const [st, setSt] = React.useState({ loading: true });
  React.useEffect(() => {
    let live = true;
    (async () => {
      try {
        const profile = await window.kanjo?.scale?.profile?.getProfile?.().catch(() => null);
        const sum = await window.kanjo.cashflow.capex.summarize();
        if (live) setSt({ loading: false, sum, currency: profile?.currency || 'USD', locale: profile?.locale || 'en-US' });
      } catch (e) { if (live) setSt({ loading: false, error: String(e?.message || e) }); }
    })();
    return () => { live = false; };
  }, []);
  const { sum, currency, locale } = st;
  const fmtDate = (iso) => new Date(iso + 'T00:00:00Z').toLocaleDateString(locale || 'en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  const ytdItems = sum ? sum.items.filter((i) => i.date.startsWith(`${sum.thisYear}-`)) : [];

  return (
    <KShell dark={dark} tab="reports" persona={persona}>
      <div style={{ height: '100%', overflowY: 'auto', paddingBottom: 24 }}>
        <KHeader back title={t('reports.capex', 'Capital expenses')} subtitle={t('reports.capexSub', 'One-off equipment & build-out · tagged on Spending')} />
        <div style={{ padding: '0 20px' }}>
          {st.error && <ErrorBanner>{st.error}</ErrorBanner>}
          {st.loading && !st.error && <SkeletonCard height={140} />}
          {sum && !st.loading && (sum.ytdCents > 0 || sum.lastYearCents > 0) ? (
            <>
              {/* YTD vs last year */}
              <div className="card" style={{ padding: 18, marginBottom: 14 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div>
                    <div className="label">{t('reports.capexYtd', '{year} · YTD').replace('{year}', String(sum.thisYear))}</div>
                    <div style={{ marginTop: 6 }}>
                      <KMoney cents={sum.ytdCents} currency={currency} locale={locale} size="xl" tone="danger" />
                    </div>
                  </div>
                  <div>
                    <div className="label">{t('reports.capexLastYear', '{year} · full year').replace('{year}', String(sum.priorYear))}</div>
                    <div style={{ marginTop: 6 }}>
                      <KMoney cents={sum.lastYearCents} currency={currency} locale={locale} style={{ fontSize: 22, fontWeight: 700, color: 'var(--ink-2)' }} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Funding → where it went (YTD) */}
              {sum.ytdCents > 0 && sum.byMerchant.length > 0 && (
                <div className="card" style={{ margin: '0 0 14px', padding: '14px 12px 12px' }}>
                  <div className="label" style={{ padding: '0 6px 8px' }}>{t('reports.capexFlow', 'Funding → purchases · {year}').replace('{year}', String(sum.thisYear))}</div>
                  <CapexFlow byMethod={sum.byMethod} byMerchant={sum.byMerchant} total={sum.ytdCents} currency={currency} locale={locale} />
                </div>
              )}

              {/* YTD items */}
              <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 14 }}>
                <div className="label" style={{ padding: '14px 16px 6px' }}>{t('reports.capexActivity', 'Purchases · {year}').replace('{year}', String(sum.thisYear))}</div>
                {ytdItems.length === 0 ? (
                  <div style={{ padding: '12px 16px', fontSize: 12.5, color: 'var(--ink-3)' }}>{t('reports.capexNone', 'No capital purchases this year yet.')}</div>
                ) : ytdItems.map((it) => (
                  <div key={it.plaidTxId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderTop: '1px solid var(--rule)' }}>
                    <span style={{ minWidth: 0, paddingRight: 12 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.merchant}</div>
                      <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
                        {fmtDate(it.date)} · {it.method === 'card' ? t('reports.capexCard', 'Credit card') : t('reports.capexCheck', 'Check / bank')}
                      </div>
                    </span>
                    <KMoney cents={it.amountCents} currency={currency} locale={locale} tone={it.amountCents < 0 ? 'positive' : 'danger'} style={{ fontSize: 14, fontWeight: 700 }} />
                  </div>
                ))}
              </div>

              <div style={{ fontSize: 11.5, color: 'var(--on-dim)', marginTop: 12, lineHeight: 1.5 }}>
                {t('reports.capexFootnote', "Capital purchases are one-off, so they're kept out of your last-30-day spending trend and the cost reports. Tag a charge as Capital Expenses on the Spending tab — a per-merchant tag also catches a later check to the same vendor.")}
              </div>
            </>
          ) : sum && !st.loading ? (
            <div className="card" style={{ padding: 20 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>{t('reports.capexEmptyTitle', 'No capital expenses tagged yet')}</div>
              <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.55, marginTop: 8 }}>
                {t('reports.capexEmptyBody', 'On the Spending tab, categorize an equipment or build-out charge as "Capital Expenses" and it lands here — kept out of your operating trend because it\'s a one-off. A per-merchant tag also captures a later check to the same vendor.')}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </KShell>
  );
}

Object.assign(window, { TaxReport, PayrollReport, CensusReport, RentReport, SuppliesReport, InsuranceReport, CapexReport });
